import { Routes } from '@angular/router';
import { environment } from '../environments/environment';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { XbrlFilesComponent } from './components/xbrl-files/xbrl-files.component';
import { MainLayoutComponent } from './layouts/main-layout/main-layout.component';
import { AuthLayoutComponent } from './layouts/auth-layout/auth-layout.component';
import { LoginComponent } from './components/auth/login/login.component';
import { isAuthenticatedGuard } from './guards/is-authenticated.guard';
import { ProfileComponent } from './components/profile/profile.component';
import { ForgotPasswordComponent } from './components/auth/forgot-password/forgot-password.component';
import { ResetLinkComponent } from './components/auth/reset-link/reset-link.component';
import { ResetPasswordComponent } from './components/auth/reset-password/reset-password.component';
import { ResetPasswordStatusComponent } from './components/auth/reset-password-status/reset-password-status.component';
import { isTokenValidGuard } from './guards/is-token-valid.guard';
import { redirectIfAuthenticatedGuard } from './guards/redirect-if-authenticated.guard';
import { UserManagementComponent } from './components/user-management/user-management.component';
import { isAdminGuard } from './guards/is-admin.guard';
import { Role1010000FilingInfoComponent } from './components/xbrl-form/role1-010000-filing-info/role1-010000-filing-info.component';
import { Role2020000ScopeOfFilingComponent } from './components/xbrl-form/role2-020000-scope-of-filing/role2-020000-scope-of-filing.component';
import { FilingFormContainerComponent } from './components/xbrl-form/filing-form-container/filing-form-container.component';
import { ErrorPageComponent } from './components/error-page/error-page.component';
import { NotFoundComponent } from './components/not-found/not-found.component';
import { NOT_FOUND_PATH } from './constants/route-paths';
import { errorPageGuard } from './guards/error-page.guard';
import { canDeactivateFilingGuard } from './guards/can-deactivate-filing.guard';
import { conversionExistsGuard } from './guards/conversion-exists.guard';
import { canUseConversionsGuard } from './guards/can-use-conversions.guard';
import { isPhase2Guard } from './guards/is-phase2.guard';
import { RouteReloadHopComponent } from './components/common/route-reload-hop/route-reload-hop.component';

export const routes: Routes = [
  { path: '', redirectTo: 'mbrs/dashboard', pathMatch: 'full' },
  { path: 'error', component: ErrorPageComponent, canActivate: [errorPageGuard] },
  {
    path: 'mbrs',
    component: MainLayoutComponent,
    children: [
      // Bare `/mbrs` consumes the whole URL at the parent, leaving an empty
      // child segment. Without this default child the layout shell renders
      // but the child <router-outlet> matches nothing — a blank content area.
      // The top-level `'' -> mbrs/dashboard` redirect only fires for `/`, so
      // it does not cover a direct visit to `/mbrs`.
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      { path: 'dashboard', component: DashboardComponent, canActivate: [canUseConversionsGuard] },
      { path: 'xbrl-files', component: XbrlFilesComponent, canActivate: [canUseConversionsGuard] },
      { path: 'profile', component: ProfileComponent },
      { path: 'reload-hop', component: RouteReloadHopComponent, data: { robots: 'noindex, nofollow' } },
      {
        path: 'user-management',
        component: UserManagementComponent,
        canActivate: [isAdminGuard],
        // Admin surface — always private. Every environment's index.*.html
        // already defaults to `noindex, nofollow` (MBRS is internal-use
        // only), so this `data.robots` tag restates the static default
        // today. It is kept as defence-in-depth: if the production
        // default is ever relaxed to `index, follow`, RobotsMetaService
        // will narrow this route back to noindex without a second edit.
        // See services/common/robots-meta.service.ts.
        data: { robots: 'noindex, nofollow' },
      },
      {
        path: 'filing',
        component: FilingFormContainerComponent,
        // Refuses a conversion that does not exist / is not the caller's
        // BEFORE the page mounts. See conversionExistsGuard.
        canActivate: [canUseConversionsGuard, conversionExistsGuard],
        canDeactivate: [canDeactivateFilingGuard],
      },
      // Phase-2-only surfaces — role1 (FilingInformation) and role2
      // (ScopeOfFiling) are hidden in Phase 1 because role0 owns the filing
      // state until conversion advances. isPhase2Guard redirects URL-poke
      // attempts back to /mbrs/filing during Phase 1.
      { path: 'filing/role1', component: Role1010000FilingInfoComponent, canActivate: [canUseConversionsGuard, isPhase2Guard] },
      { path: 'filing/role2', component: Role2020000ScopeOfFilingComponent, canActivate: [canUseConversionsGuard, isPhase2Guard] }
    ],
    canActivate: [isAuthenticatedGuard],
    // Re-checks the session on EVERY page change inside /mbrs, not only on
    // entry, so a session that ended mid-use sends the user to login BEFORE
    // the destination page loads (P.O., 2026-10-08). See isAuthenticatedGuard.
    canActivateChild: [isAuthenticatedGuard],
  },
  {
    path: 'auth',
    component: AuthLayoutComponent,
    // `redirectIfAuthenticatedGuard` sits on the GROUP, not on individual
    // children, because every page under /auth is a pre-authentication page:
    // a user with a live session has no business on any of them.
    //
    // Cross-tab: only LoginComponent reloads when a sibling tab signs in, and
    // the reload works because this guard is on the route it reloads into. The
    // other four pages (forgot-password, reset-link, reset-password,
    // reset-password-status) do NOT follow a sibling sign-in — the user stays
    // mid-reset — and reach the dashboard via "Back to Login", which navigates
    // to /auth/login through this same guard.
    //
    // Registered as `canActivateChild`, NOT `canActivate`: a parent's
    // `canActivate` runs only when the parent is first entered, so a sibling
    // navigation inside /auth ("Back to Login" from a reset page) never re-ran
    // it and the tab stayed on the login page despite a live session.
    // `canActivateChild` runs on every child navigation, including the first
    // entry, and still runs before `isTokenValidGuard` on reset-password.
    //
    // Guarding the group rather than each child also means a future /auth page
    // inherits the behaviour instead of having to remember it. The guard never
    // trusts the cross-tab broadcast — it re-verifies against the backend — and
    // it short-circuits for a forced logout so `forceLogout()` can still land
    // on /auth/login.
    //
    // Accepted trade-off: an already-signed-in user who opens a password-reset
    // email link is now sent to the dashboard instead of the reset form. That
    // is the same policy /auth/login has always had, and the in-session path
    // for this is My Profile -> Change Password.
    canActivateChild: [redirectIfAuthenticatedGuard],
    children: [
      { path: 'login', component: LoginComponent },
      { path: 'forgot-password', component: ForgotPasswordComponent },
      { path: 'reset-link', component: ResetLinkComponent },
      {
        path: 'reset-password',
        component: ResetPasswordComponent,
        canActivate: [isTokenValidGuard],
      },
      {
        path: 'reset-password-status',
        component: ResetPasswordStatusComponent,
      },
    ],
  },
  // Developer-only logs viewer area. Lazy-loaded — first lazy route in
  // this repo. The URL prefix `dev-{slug}` is a shared build-time constant
  // synchronised with backend appsettings.json (DevArea:Slug). When
  // devAreaSlug is empty the route is registered under path `dev-` which
  // is effectively disabled (no dev area deployed).
  {
    path: `dev-${environment.devAreaSlug}`,
    loadChildren: () => import('./components/dev/dev.routes').then((devRoutes) => devRoutes.DEV_ROUTES),
    // Developer-only surface — always private in every environment.
    // Every environment's index.*.html defaults to `noindex, nofollow`
    // today (MBRS is internal-use only), so this restates the static
    // default. Kept as defence-in-depth in case the production default
    // is ever relaxed; RobotsMetaService inherits this onto every
    // child route unless a child explicitly overrides it. See
    // services/common/robots-meta.service.ts.
    data: { robots: 'noindex, nofollow' },
  },
  // Explicit 404, rendered by guards for a page that exists but the user may
  // not see (isAdminGuard). They navigate here with `skipLocationChange`, so
  // the address bar keeps the URL the user typed and the result is
  // indistinguishable from the catch-all below. See constants/route-paths.ts.
  {
    path: NOT_FOUND_PATH,
    component: NotFoundComponent,
    data: { robots: 'noindex, nofollow' },
  },
  // Catch-all 404. Must be the LAST entry — any route added after this
  // will never match. Keeps unknown URLs from rendering a blank page.
  // Restates the static `noindex, nofollow` default — kept explicit so
  // broken-link landing pages stay private even if the production
  // default is ever relaxed.
  {
    path: '**',
    component: NotFoundComponent,
    data: { robots: 'noindex, nofollow' },
  },
];
